import { ConnectionSourcePackageTypeEnum, requiresAPIData, requiresSynchronizeInsurerData } from 'app/types';
import { TranslationKeys } from 'app/translations';

export enum SynchronizeInsurerModuleTypeEnum {
  FetchSettingsFromInsurer = 'insurance_brydge_fetch_settings_from_insurer',
  ManualSynchronizeInsurerData = 'insurance_brydge_manual_synchronize_insurer_data_module',
}

export const SYNCHRONIZE_MANUAL_EMPLOYEES_SOURCE = 'synchronize_manual_employees';

export enum SynchronizeInsurerOptionKeyEnum {
  ConnectionSettings = 'connection-settings',
  EmployeeData = 'employee-data',
  RunAll = 'run-all',
}

export type SynchronizeInsurerParameters = {
  source?: string;
  runAll?: boolean;
};

export type SynchronizeInsurerOption = {
  key: SynchronizeInsurerOptionKeyEnum;
  titleMessageId: string;
  moduleType: SynchronizeInsurerModuleTypeEnum;
  parameters: SynchronizeInsurerParameters;
  requiresConfirmation: boolean;
};

const SYNCHRONIZE_CONNECTION_SETTINGS_OPTION: SynchronizeInsurerOption = {
  key: SynchronizeInsurerOptionKeyEnum.ConnectionSettings,
  titleMessageId: TranslationKeys.button_synchronizeConnectionSettings,
  moduleType: SynchronizeInsurerModuleTypeEnum.FetchSettingsFromInsurer,
  parameters: {},
  requiresConfirmation: false,
};

const SYNCHRONIZE_EMPLOYEE_DATA_OPTION: SynchronizeInsurerOption = {
  key: SynchronizeInsurerOptionKeyEnum.EmployeeData,
  titleMessageId: TranslationKeys.button_synchronizeAndCreateEmployeeData,
  moduleType: SynchronizeInsurerModuleTypeEnum.ManualSynchronizeInsurerData,
  parameters: { source: SYNCHRONIZE_MANUAL_EMPLOYEES_SOURCE },
  requiresConfirmation: true,
};

const SYNCHRONIZE_RUN_ALL_MANUAL_OPTION: SynchronizeInsurerOption = {
  key: SynchronizeInsurerOptionKeyEnum.RunAll,
  titleMessageId: TranslationKeys.button_synchronizeRunAll,
  moduleType: SynchronizeInsurerModuleTypeEnum.FetchSettingsFromInsurer,
  parameters: { runAll: true, source: SYNCHRONIZE_MANUAL_EMPLOYEES_SOURCE },
  // Includes the employee data synchronization, so the same warning applies.
  requiresConfirmation: true,
};

const SYNCHRONIZE_RUN_ALL_API_OPTION: SynchronizeInsurerOption = {
  key: SynchronizeInsurerOptionKeyEnum.RunAll,
  titleMessageId: TranslationKeys.button_synchronizeRunAll,
  moduleType: SynchronizeInsurerModuleTypeEnum.FetchSettingsFromInsurer,
  parameters: {},
  requiresConfirmation: false,
};

const SYNCHRONIZE_INSURER_OPTIONS_BY_CONNECTION_TYPE: Partial<
  Record<ConnectionSourcePackageTypeEnum, SynchronizeInsurerOption[]>
> = {
  [ConnectionSourcePackageTypeEnum.Manual]: [
    SYNCHRONIZE_CONNECTION_SETTINGS_OPTION,
    SYNCHRONIZE_EMPLOYEE_DATA_OPTION,
    SYNCHRONIZE_RUN_ALL_MANUAL_OPTION,
  ],
  [ConnectionSourcePackageTypeEnum.API]: [SYNCHRONIZE_CONNECTION_SETTINGS_OPTION, SYNCHRONIZE_RUN_ALL_API_OPTION],
};

export type SynchronizeInsurerConnection = {
  connectionType?: string | null;
  packages?: { packageType?: string | null }[] | null;
  product?: { credential?: { packageType?: string | null } | null } | null;
};

/**
 * Resolves which synchronize option set applies to a connection.
 * Manual and API are mutually exclusive; Manual takes precedence.
 * API connections store the concrete source package (e.g. 'afas') as connectionType, not 'api'.
 */
export const resolveSynchronizeInsurerConnectionType = (
  connection?: SynchronizeInsurerConnection | null,
): ConnectionSourcePackageTypeEnum.Manual | ConnectionSourcePackageTypeEnum.API | null => {
  if (!connection) return null;

  if (connection.connectionType === ConnectionSourcePackageTypeEnum.Manual) {
    return requiresSynchronizeInsurerData(connection.product?.credential?.packageType ?? undefined)
      ? ConnectionSourcePackageTypeEnum.Manual
      : null;
  }

  const isApiConnection =
    requiresAPIData(connection.connectionType ?? undefined) ||
    (connection.packages ?? []).some(pkg => requiresAPIData(pkg.packageType ?? undefined));

  return isApiConnection ? ConnectionSourcePackageTypeEnum.API : null;
};

export const getSynchronizeInsurerOptions = (connectionType?: string | null): SynchronizeInsurerOption[] => {
  if (!connectionType) return [];

  return SYNCHRONIZE_INSURER_OPTIONS_BY_CONNECTION_TYPE[connectionType as ConnectionSourcePackageTypeEnum] || [];
};
